import { BlurEvent, ChangeEvent, DataSourceMode, Layout, Snackbar, Switch, Textbox } from "@mcleod/components";
import { AutogenLayoutContact } from "./autogen/AutogenLayoutContact";
import { Api, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

const viewWebUserURL = "lme/general/WebUser?mode=update&key=";
const PRIMARY_CONTACT_MESSAGE = "Primary contact must be active";

export class Contact extends AutogenLayoutContact {
    public primaryContactExists: boolean = false;

    override onLoad(): void | Promise<void> {
        this.layoutComments.displayCopyOrderFields = false
        this.textboxOptOutDate.addBlurListener((event) => {
            if (!StringUtil.isEmptyString(this.textboxOptOutDate.text)) {
                const optOutDate = new Date(this.textboxOptOutDate.text);
                const currentDate = new Date();
                if (optOutDate <= currentDate) {
                    this.switchSMSOptIn.checked = false;
                }
            }
        });

        this.switchSMSOptIn.addChangeListener((event) => {
            if(event.target.checked) {
                this.textboxOptOutDate.clear();
                this.sourceContacts.activeRow?.set("opt_out_date",null);
            }
        });
    }

    switchCheckActiveOnChange(event: ChangeEvent) {
        if (this.mainDataSource.activeRow.get("is_active") == "N") {
            this.mainDataSource.activeRow.set("is_primary_contact", "N");
            this.switchPrimaryContact.enabled = false;
            this.switchPrimaryContact.disabledTooltip = PRIMARY_CONTACT_MESSAGE;
        }
        else {
            this.switchPrimaryContact.enabled = true;
        }
    }

    sourceContactsOnDisplay() {
        const webUserId = this.mainDataSource.activeRow?.getString("users.id", null);
        this.textboxWebUserId.enabled = this.textboxWebUserId.required =
        this.textboxWebPassword.enabled = this.textboxWebPassword.required =
        this.textboxLoginId.enabled = this.switchWebAccess.checked && webUserId == null;

        if (this.mainDataSource.activeRow?.get("is_primary_contact") == "Y") {
            this.switchIsActive.enabled = false;
            this.switchIsActive.disabledTooltip = PRIMARY_CONTACT_MESSAGE;
        }
        else if (this.mainDataSource.activeRow?.get("is_active") == "N") {
            this.switchPrimaryContact.enabled = false;
            this.switchPrimaryContact.disabledTooltip = PRIMARY_CONTACT_MESSAGE;
        }
    }

    textboxNameOnBlur() {
        this.setButtonViewUserEnabled();
    }

    async textboxEmailOnBlur(event: BlurEvent) {
        const email = this.mainDataSource.activeRow?.getString("email", null);
        const webUserId = this.mainDataSource.activeRow?.getString("users.id", null);
        const loginId = this.mainDataSource.activeRow?.getString("users.login_id", null);
        this.setButtonViewUserEnabled();
        if (event.changedWhileFocused && email && this.switchWebAccess.checked && (webUserId == null || loginId == null)) {
            await this.setAutogenValues(email, webUserId, loginId);
        }
    }

    async switchWebAccessOnChange(event: ChangeEvent) {
        const switchWebAccess = event.target as Switch;
        const webUserId = this.mainDataSource.activeRow?.getString("users.id", null);
        const webPassword = this.mainDataSource.activeRow?.getString("users.web_password", null);
        const loginId = this.mainDataSource.activeRow?.getString("users.login_id", null);
        const email = this.mainDataSource.activeRow?.getString("email", null);

        this.textboxEmail.required = switchWebAccess.checked;
        this.textboxWebPassword.required = switchWebAccess.checked;
        this.textboxWebUserId.enabled = switchWebAccess.checked && (webUserId == null || this.activeRow?.getChangedData().hasOwnProperty("users.id"));
        this.textboxWebPassword.enabled = switchWebAccess.checked && (webUserId == null || this.activeRow?.getChangedData().hasOwnProperty("users.id"));
        this.textboxLoginId.enabled = switchWebAccess.checked && (loginId == null || this.activeRow?.getChangedData().hasOwnProperty("users.login_id"));
        this.textboxWebUserId.text = switchWebAccess.checked ? webUserId : null;
        this.textboxWebPassword.text = switchWebAccess.checked ? webPassword : null;
        this.textboxLoginId.text = switchWebAccess.checked ? loginId : null;
        this.textboxWebUserId.required = this.textboxLoginId.required = switchWebAccess.checked;

        if (email && switchWebAccess.checked && (webUserId == null || loginId == null)) {
            await this.setAutogenValues(email, webUserId, loginId);
        }

        if (!this.activeRow?._appending) {
            this.setButtonViewUserEnabled();
            this.buttonViewUser.color = this.buttonViewUser.enabled ? "primary" : "stroke.secondary";
            this.buttonViewUser.tooltip = !this.buttonViewUser.enabled ? "Please save updates to this record before viewing web user" : null;
        }
    }

    async setAutogenValues(email: string, webUserId: string, loginId: string) {
        const response = await Api.post("lme/general/validate-web-user", {
            user_id: webUserId,
            login_id: loginId ?? email,
            autogen: true
        });

        if (response) {
            const data = response?.data[0];
            const generatedUserId: string = data?.autogen_user_id;
            const message: string = data?.message;
            if (data.duplicate_web_user_id) {
                this.textboxWebUserId.clear();
                this.mainDataSource.activeRow?.set("users.id", null);
            } else {
                this.mainDataSource.activeRow?.set("users.id", webUserId ?? generatedUserId);
            }

            if (data.duplicate_login_id) {
                this.textboxLoginId.clear();
                this.mainDataSource.activeRow?.set("users.login_id", null);
            } else {
                this.mainDataSource.activeRow?.set("users.login_id", loginId ?? email);
            }

            if (message) {
                Snackbar.showWarningSnackbar(message, { persist: true });
            }
        }
    }

    buttonViewUserOnClick() {
        const userId = this.activeRow?.getString("users.id", null);
        Navigation.navigateTo(viewWebUserURL + userId, { newTab: true });
    }

    private setButtonViewUserEnabled() {
        this.buttonViewUser.enabled = this.switchWebAccess.checked && !this.activeRow?.hasChanged();
        this.buttonViewUser.tooltip = !this.buttonViewUser.enabled ? "Please save updates to this record before viewing web user" : null;
    }

    async textboxWebUserIdOnBlur(event: BlurEvent) {
        const webUserId = this.mainDataSource.activeRow?.getString("users.id", null);
        if (event.changedWhileFocused && webUserId) {
            const response = await Api.post("lme/general/validate-web-user", {
                user_id: webUserId,
                login_id: null,
                autogen: false
            });

            if (response) {
                const data = response?.data[0];
                const message: string = data?.message;
                if (message) {
                    Snackbar.showWarningSnackbar(message, { persist: true });
                    this.textboxWebUserId.clear();
                    this.mainDataSource.activeRow?.set("users.id", null);
                }
            }
        }
    }

    async textboxLoginIdOnBlur(event: BlurEvent) {
        const loginId = this.mainDataSource.activeRow?.getString("users.login_id", null);
        if (event.changedWhileFocused && loginId) {
            const response = await Api.post("lme/general/validate-web-user", {
                user_id: null,
                login_id: loginId,
                autogen: false
            });

            if (response) {
                const data = response?.data[0];
                const message: string = data?.message;
                if (message) {
                    Snackbar.showWarningSnackbar(message, { persist: true });
                    this.textboxLoginId.clear();
                    this.mainDataSource.activeRow?.set("users.login_id", null);
                }
            }
        }
    }

    switchPrimaryContactOnChange(event: ChangeEvent) {
        const modelRow = (event.target as Switch).getRelevantModelRow();
        if (modelRow == null) return;
        this.textboxEmail.required = modelRow.getBoolean("is_primary_contact", false);
        if (event.userInitiatedChange && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            if (event.newValue === false) {
                this.switchIsActive.enabled = true;
                return;
            }

            if (this.primaryContactExists) {
                CommonDialogs.showYesNo("Are you sure you want to change the primary contact?").then(result => {
                    if (!result) {
                        modelRow.set("is_primary_contact", false);
                        this.textboxEmail.required = false;
                        event.preventDefault();
                    }
                    else {
                        this.switchIsActive.enabled = false;
                        this.switchIsActive.disabledTooltip = "Primary contact must be active";
                    }
                });
            }
        }
    }

    textboxVerifiedStatusOnChange(event: ChangeEvent) {
        if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            this.textboxEmail.required = (event.newValue != null && event.newValue !== "")
        }
    }

    textboxRecOpsUserIdBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
    }

    public applyLookupModelData(textbox: Textbox, modelRow: ModelRow): Promise<void> {
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        return lookupLayout.ensureLoaded().then(() => {
            const fieldValue = modelRow.get(textbox.field);
            const filter = {
                [textbox.lookupModelResultField]: (fieldValue.indexOf(",") >= 0) ? `in ${fieldValue}` : fieldValue
            };
            return lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows[0]);
                } else if (response?.modelRows?.length > 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows.map(row => ({
                        [textbox.lookupModelDisplayField]: row.get(textbox.lookupModelDisplayField),
                        [textbox.lookupModelResultField]: row.get(textbox.lookupModelResultField)
                    })));
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), lookupModelData);
                }
            });
        })
    }

    /** Checks web_user_type against RowUsers.portalUserTypeDBValues (not contact parent_row_type). */
    public static isPortalUser(webUserType: string | null | undefined): boolean {
        return webUserType != null && ["R", "C", "D", "5", "L", "S"].includes(webUserType);
    }
}
